import { Navigate, useLocation } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import { useAuthStore } from '@store/authStore';
import {
  trackClarityPageView,
  identifyUserInClarity,
  upgradeClaritySession,
  trackClarityEvent,
} from '@services/clarity';
import { analytics } from '@services/analytics';
import MetaTags from '@components/seo/MetaTags';

interface RouteWrapperProps {
  children: React.ReactNode;
  extraWide?: boolean;
}

// Protected route wrapper component
export default function ProtectedRoute({
  children,
  extraWide = false,
}: RouteWrapperProps) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  // Track page views and user identification for protected routes
  useEffect(() => {
    if (isAuthenticated && user?.id && !isLoading) {
      // Identify user in Clarity
      identifyUserInClarity(user);

      // Also identify user in our analytics system
      analytics.identifyUser(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        onboarding_completed: user.onboardingCompleted,
        email_confirmed: !!user.emailConfirmedAt,
      });

      // Track page view with authentication context
      const pageName = location.pathname.replace('/', '') || 'dashboard';

      // Add enhanced context based on route
      let additionalContext: Record<string, string | number | boolean> = {
        route_type: 'protected',
        user_authenticated: true,
        user_id: user.id,
        onboarding_completed: user.onboardingCompleted,
      };

      // Add route-specific context for protected pages
      if (location.pathname.includes('/actions/')) {
        const actionMatch = location.pathname.match(/\/actions\/(.+)/);
        if (actionMatch) {
          additionalContext = {
            ...additionalContext,
            action_type: actionMatch[1],
            page_section: 'actions',
          };
          // Auto-upgrade session for action pages
          upgradeClaritySession(`action_${actionMatch[1]}`);
        }
      }

      trackClarityPageView(pageName, additionalContext);

      // Also track in our analytics system
      analytics.trackPageView(pageName, {
        ...additionalContext,
        pathname: location.pathname,
      });

      // Track business-relevant page viewed events
      if (location.pathname === '/profile') {
        trackClarityEvent('profile_page_viewed');
      } else if (location.pathname === '/support') {
        trackClarityEvent('support_page_viewed');
        // Auto-upgrade session for support requests (indicates potential issues)
        upgradeClaritySession('support_request');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, isLoading, location.pathname]);

  // Don't redirect while checking auth status
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={48} color="primary" />
      </Box>
    );
  }

  // Only redirect if we're sure the user isn't authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <MetaTags noIndex />
      <div
        style={{
          maxWidth: extraWide ? '1600px' : '100%',
          margin: extraWide ? '0 auto' : undefined,
          width: '100%',
        }}
      >
        {children}
      </div>
    </>
  );
}

// Public route wrapper component
export function PublicRoute({
  children,
  extraWide = false,
}: RouteWrapperProps) {
  const { isLoading, user } = useAuthStore();
  const location = useLocation();

  // Track page views for public routes
  useEffect(() => {
    if (!isLoading) {
      // Track page view with public route context
      const pageName = location.pathname.replace('/', '') || 'home';

      // Add enhanced context based on route
      let additionalContext: Record<string, string | number | boolean> = {
        route_type: 'public',
        user_authenticated: !!user?.id,
        user_id: user?.id || 'anonymous',
      };

      // Add route-specific context
      if (location.pathname.includes('/onboarding')) {
        const stepMatch = location.pathname.match(/\/step\/(\d+)/);
        if (stepMatch) {
          additionalContext = {
            ...additionalContext,
            step_number: parseInt(stepMatch[1]),
            page_section: 'onboarding',
            visitor_type: 'lead',
          };
          // Auto-upgrade session for onboarding flow
          upgradeClaritySession('onboarding_flow');
        }
      } else if (location.pathname === '/') {
        additionalContext = {
          ...additionalContext,
          page_section: 'home',
          visitor_type: 'anonymous',
        };
        // Auto-upgrade session for landing page (conversion intent tracking)
        upgradeClaritySession('landing_page_view');
      }

      trackClarityPageView(pageName, additionalContext);

      // Also track in our analytics system
      analytics.trackPageView(pageName, {
        ...additionalContext,
        pathname: location.pathname,
      });

      // Track key conversion pages for public routes
      if (location.pathname === '/estimate') {
        analytics.trackConversion('estimate_page_viewed', undefined, {
          conversion_type: 'page_view',
          page_name: 'Tax Estimate',
        });
      } else if (location.pathname === '/estimate-sent') {
        analytics.trackConversion('onboarding_completed', undefined, {
          conversion_type: 'onboarding_completion',
          page_name: 'Estimate Sent',
        });
      } else if (location.pathname === '/booking-success') {
        analytics.trackConversion('call_booked', undefined, {
          conversion_type: 'call_booking',
          page_name: 'Booking Confirmation',
        });
      } else if (
        location.pathname.includes('/calendar') ||
        location.pathname === '/book-call'
      ) {
        analytics.trackConversion('booking_page_viewed', undefined, {
          conversion_type: 'page_view',
          page_name: pageName,
        });
      }
    }
  }, [isLoading, location.pathname, user?.id]);

  // Don't redirect while checking auth status
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={48} color="primary" />
      </Box>
    );
  }

  // Only redirect if we're sure the user is authenticated
  // if (isAuthenticated) {
  //   return <Navigate to="/dashboard" replace />;
  // }

  return (
    <>
      <MetaTags />
      <div
        style={{
          maxWidth: extraWide ? '1600px' : '100%',
          margin: extraWide ? '0 auto' : undefined,
          width: '100%',
        }}
      >
        {children}
      </div>
    </>
  );
}

// Not found route wrapper component
export function NotFoundRoute({ children }: RouteWrapperProps) {
  const location = useLocation();

  // Track 404 page views
  useEffect(() => {
    trackClarityPageView('404', {
      route_type: 'error',
      requested_path: location.pathname,
      error_type: 'not_found',
    });
  }, [location.pathname]);

  return (
    <>
      <MetaTags noIndex title="Page Not Found - Fieldvest" />
      {children}
    </>
  );
}
